import './image-panel.less';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Message } from '@meitu/saas-ui';
import { getCutoutResult } from '@meitu/saas-editor-core/es/plugins/image-cutout/utils';
import {
    IImageLayerProps,
    IImageProps,
    ILayerProps,
    ImgModeType,
    LayerType,
    MASK_DEFAULT_IMG,
} from '@meitu/saas-editor-core/es/version-types';
import CropImgPanel from '@meitu/saas-editor-ui/es/panels/image-layer-panel/components/crop-img-panel/crop-img-panel';
import { getPresetEffectList } from '@meitu/saas-editor-ui/es/panels/image-layer-panel/components/effect-panel/constant';
import ImageEffectPanel from '@meitu/saas-editor-ui/es/panels/image-layer-panel/components/effect-panel/index';
import useImageGlRenderClose from '@meitu/saas-editor-ui/es/panels/image-layer-panel/hooks/use-close-image-gl-render';
import { useMattingPluginHelper } from '@/editor/extensions/ai-matting/use-matting-plugin-helper';
import { events, HIDE_PANEL_ADJUST } from '@/events-bus';
import { usePanelLayerId } from '@/hooks';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useStoreActions, useStoreState } from '@/store';
import { getLayerType, HandleHbLayersFunc } from '@/utils/stat-track';
import useReplaceLayer from '../../hooks/use-replace-layer';
import UploadReplace from './components/upload-replace/upload-replace';
import ImageAdjust from './image-adjust-panel/image-adjust';
import ImageMainPanel from './image-main-panel/image-main-panel';
import ImageMaskPanel from './image-mask-panel/image-mask-panel';
import { IImgPanelType } from './type';

export interface ICategory {
    name: string;
    id: string;
}

export type Image = {
    id: string;
    url?: string;
    width: number;
    height: number;
    thumbnail: string;
    ext?: string;
};

const tabsList = () => {
    const { $t } = getLocaleStore();

    return [
        {
            name: $t((v) => v.panel.imagePanel.aiImage),
            tabKey: 'AIImage',
        },
        {
            name: $t((v) => v.panel.imagePanel.imageSetting),
            tabKey: 'ImageSet',
        },
    ];
};

const ImagePanel = () => {
    const [replaceImageNum, setReplaceImageNum] = useState<number>(0); // 用于标记图片双击替换操作
    const theme = useStoreState((state) => state.editorModel.theme);
    const setProcessingMattingImgInfo = useStoreActions((actions) => actions.pluginModel.setProcessingMattingImgInfo);
    const clearProcessingMattingImgInfo = useStoreActions(
        (actions) => actions.pluginModel.clearProcessingMattingImgInfo,
    );
    const setIsDisableAppAndShowPanel = useStoreActions((actions) => actions.editorModel.setIsDisableAppAndShowPanel);
    const mattingPluginHelper = useMattingPluginHelper();
    const getRecordInfo = useStoreActions((actions) => actions.recordModel.getRecordInfo);
    const layerId = usePanelLayerId();

    const [panelType, setPanelType] = useState<IImgPanelType | null>(null);

    const uploadRef = useRef<any>(null);

    const is_search = useStoreState((state) => state.searchModel.is_search);

    const { replaceLayer } = useReplaceLayer();
    const { $t } = useLocaleStore();

    // 埋点信息相关的
    const isPhoto = layerId ? editor.imageLayer.checkIsPhoto(layerId) : false;
    const isAiText = layerId ? editor.imageLayer.checkIsAiText(layerId) : false;
    const firstId = isPhoto ? 4 : isAiText ? 6 : 2;

    const statLayerTypeRef = useRef<number>(0);

    useImageGlRenderClose(editor);

    useEffect(() => {
        return () => {
            if (!layerId) {
                return;
            }

            editor.imageLayer.showImageEffectRenderState(layerId, false);
        };
    }, []);

    /**
     * 选择显示的图片面板
     *
     * @param type - 图片面板类型
     */
    const selectPanel = useCallback((type: IImgPanelType | null) => {
        setPanelType(type);
    }, []);

    useEffect(() => {
        setPanelType(null);
    }, [layerId]);

    useEffect(() => {
        const eventId = editor.onDelete(() => {
            if (!layerId) return;
            const activeLayer = editor.stage.getLayer(layerId);
            const { layerType } = activeLayer;
            const { url, maskURL } = activeLayer.data as IImageLayerProps;

            if (layerType === LayerType.IMAGE_LAYER && maskURL && url?.indexOf(MASK_DEFAULT_IMG().url)) {
                editor.imageLayer.clearImage(activeLayer.id);
                return true;
                // 调用该方法会阻止默认热键删除功能
            }
            return false;
        }, 100);

        return () => {
            editor.off(eventId);
        };
    }, [layerId]);

    useEffect(() => {
        if (!layerId) return;
        const activeLayer = editor.stage.getLayer(layerId);

        if (activeLayer) {
            const res = getLayerType(activeLayer);
            statLayerTypeRef.current = res.layerType;
        }
    }, [layerId]);

    useEffect(() => {
        const eventId = editor.stage.onLayerDoubleClick((selectLayerId, point) => {
            if (!layerId) return;
            const activeLayer = editor.stage.getLayer(layerId);
            if (selectLayerId !== layerId) {
                return;
            }

            if (activeLayer?.__filterPreview) return;

            uploadRef.current?.open();

            HandleHbLayersFunc({
                firstId: firstId,
                secondId: 3,
                isClick: true,
                recordId: (getRecordInfo().id || '').toString(),
                layerType: statLayerTypeRef.current,
                canvas_type: editor.stage.getType(),
            });

            // 隐藏调色面板
            events.emit(HIDE_PANEL_ADJUST);
        });

        return () => {
            editor.off(eventId);
        };
    }, [layerId]);

    useEffect(() => {
        if (panelType === IImgPanelType.EFFECT) {
            getPresetEffectList('').forEach((v) => {
                window._stat_track('tpbj_material_show', {
                    type_id: 1059,
                    material_id: v.id,
                });
            });
        }
    }, [panelType]);

    const funcStatTrack = ({ useId, clickId }: any) => {
        if (!layerId) return;
        HandleHbLayersFunc({
            firstId: firstId,
            secondId: useId,
            isClick: clickId ? true : false,
            isUse: useId ? true : false,
            recordId: (getRecordInfo().id || '').toString(),
            activeLayer: editor.stage.getLayer(layerId),
            layerType: statLayerTypeRef.current,
            canvas_type: editor.stage.getType(),
        } as any);
        window._stat_track('tpbj_material_click', {
            type_id: 1059,
            material_id: useId,
        });
        window._stat_track('tpbj_material_tick', {
            type_id: 1059,
            material_id: useId,
        });
    };

    const effectListClickStatTrack = (id: string) => {
        window._stat_track('hb_text_edit_yes_data', {
            effect: id,
        });
    };

    const handleImageMatting = async (curLayer: ILayerProps) => {
        try {
            editor.tip.showMask({
                loading: true,
                text: $t((v) => v.panel.imagePanel.tips.matting),
            });
            const newInfo = cloneDeep(curLayer);
            const originLayer = cloneDeep(curLayer);
            const data = curLayer.data as IImageProps;
            try {
                const {
                    url,
                    error,
                    imageBaseMaskURL: maskURL,
                    mattingType,
                    useBlurFilterRun,
                    // 算法前景估计图
                    foregroundEstimateImageURL,
                } = await getCutoutResult(data.url, true, isIntl);
                if (url) {
                    (newInfo.data as IImageLayerProps).url = url;
                    (newInfo.data as IImageLayerProps).effect = null;

                    editor.imageLayer.updateLayer(newInfo);

                    editor.imageCutout.open(
                        {
                            resultImage: url,
                            originImage: (originLayer.data as IImageLayerProps).url,
                            isCutoutMainLayer: false,
                        },
                        {
                            cancelClickHandler: async () => {
                                window._stat_track('kt_hotkey_user_decision', {
                                    oper_type: 0,
                                });
                                editor.imageCutout.close();
                                clearProcessingMattingImgInfo();
                            },
                            confirmClickHandler: async () => {
                                window._stat_track('kt_hotkey_user_decision', {
                                    oper_type: 1,
                                });
                                editor.imageCutout.close();
                                clearProcessingMattingImgInfo();
                            },
                            optimizeClickHandler: () => {
                                mattingPluginHelper.show();
                            },
                            beforeConfirm: () => Promise.resolve(true),
                        },
                        {
                            leftFreeNum: 0,
                            freeNum: 0,
                            isFree: true,
                            isIntl,
                            isSilentMode: false,
                            numLeft: 0,
                            isInternalUserVip: true,
                        },
                    );
                    editor.tip.hideMask();

                    // 缓存当前抠图原图和抠图识别信息
                    setProcessingMattingImgInfo({
                        url: data.url,
                        name: '',
                        size: 0,
                        width: data.naturalHeight,
                        height: data.naturalHeight,
                        mattingInfo: {
                            maskURL,
                            mattingType,
                            useBlurFilterRun,
                            foregroundEstimateImageURL,
                        },
                    });
                    window._stat_track('kt_cutout_succeed', {
                        first_source: '海报设计',
                    });
                } else {
                    if (error) {
                        Message.error(error);
                    }
                    window._stat_track('kt_fail_show', {
                        first_source: '海报设计',
                        class: 1,
                    });
                    editor.tip.hideMask();
                }
            } catch (e) {
                editor.tip.hideMask();
            }
        } catch (error) {
            if (error) {
                const isNetworkError = JSON.stringify(error).indexOf('Network Error') > -1;
                if (isNetworkError) {
                    Message.error($t((v) => v.panel.imagePanel.messageError.networkError));
                }
            }
            window._stat_track('kt_fail_show', {
                first_source: '海报设计',
                class: 0,
            });
            editor.imageCutout.close();
            clearProcessingMattingImgInfo();
            editor.tip.hideMask();
            console.log(error, '>>>>error');
        }
    };

    useEffect(() => {
        if (!layerId) return;
        const activeLayer = editor.stage.getLayer(layerId);
        if (replaceImageNum && !activeLayer.__isCropImage && activeLayer?.mode === ImgModeType.matting) {
            // 自动抠图
            handleImageMatting(activeLayer);
        }
    }, [replaceImageNum]);

    // ATTENTION: 统一编辑器交互，当对图层有做基础操作时，自动关闭二级属性面板
    useEffect(() => {
        return editor.onBaseOperateChange((isBaseOperate) => {
            const layer = editor.active.getPick();

            if (!layer || layer.layerType !== LayerType.IMAGE_LAYER) {
                return;
            }

            selectPanel(null);
        });
    }, []);

    if (!layerId) return null;

    const isAiPPT = false;

    return (
        <div className="c-images-panel">
            {/* 图片属性面板主页面 */}
            <ImageMainPanel
                selectPanel={selectPanel}
                isAiPPT={isAiPPT}
                panelType={panelType}
                onImageMatting={handleImageMatting}
                onUpdateReplaceImageNum={() => setReplaceImageNum(replaceImageNum + 1)}
            />

            {/* 特效 */}
            {panelType === IImgPanelType.EFFECT ? (
                <ImageEffectPanel
                    setPanel={selectPanel}
                    funcStatTrack={funcStatTrack}
                    effectListClickStatTrack={effectListClickStatTrack}
                    editor={editor}
                    theme={theme}
                />
            ) : null}

            {/* 裁剪 */}
            {panelType === IImgPanelType.CROP ? (
                <CropImgPanel
                    editor={editor}
                    setPanel={selectPanel}
                    setIsDisableAppAndShowPanel={setIsDisableAppAndShowPanel}
                />
            ) : null}

            {/* 调色 */}
            {panelType === IImgPanelType.ADJUST && <ImageAdjust setPanel={selectPanel} layerId={layerId} />}

            {/* 蒙版 */}
            {panelType === IImgPanelType.MASK && <ImageMaskPanel setPanel={selectPanel} firstId={firstId} />}
            <UploadReplace
                ref={uploadRef}
                layerId={layerId}
                onUpdateReplaceImageNum={() => setReplaceImageNum(replaceImageNum + 1)}
            />
        </div>
    );
};

export default React.memo(ImagePanel);
